import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { students as seedStudents } from "../data/students";
import { groups as seedGroups } from "../data/groups";
import { mentors as seedMentors, retiredMentorIds } from "../data/mentors";
import type { GroupProfile, GroupScore, Mentor, Student, VideoEntry } from "../types/hackathon";
import { deleteVideo, saveVideo } from "../utils/videoStore";
import { generateId, generateToken } from "../utils/tokens";
interface HackathonState {
  students: Student[];
  mentors: Mentor[];
  groups: GroupProfile[];
  videos: VideoEntry[];
  scores: Record<number, GroupScore>;
  streamUrl: string;
  pitchOrder: number[];
  currentPitch: number;
  pitched: number[];
}
export type NewStudent = Omit<Student, "id" | "inviteToken" | "joinedAt">;
export type NewMentor = Omit<Mentor, "id" | "inviteToken" | "joinedAt">;
interface HackathonContextValue {
  state: HackathonState;
  addStudent: (student: NewStudent) => void;
  updateStudent: (id: string, patch: Partial<Student>) => void;
  removeStudent: (id: string) => void;
  addMentor: (mentor: NewMentor) => void;
  updateMentor: (id: string, patch: Partial<Mentor>) => void;
  removeMentor: (id: string) => void;
  updateGroup: (id: number, patch: Partial<GroupProfile>) => void;
  toggleMilestone: (groupId: number, milestoneId: string) => void;
  addVideo: (entry: VideoEntry, file: File) => Promise<void>;
  removeVideo: (groupId: number, day: number) => Promise<void>;
  setScore: (groupId: number, score: GroupScore) => void;
  clearScores: () => void;
  setStreamUrl: (url: string) => void;
  setPitchOrder: (order: number[]) => void;
  setCurrentPitch: (index: number) => void;
  markPitched: (groupId: number) => void;
}
const STORAGE_KEY = "hackathon-state-v1";
const initialState: HackathonState = {
  students: seedStudents,
  mentors: seedMentors,
  groups: seedGroups.map(g => ({
    id: g.id,
    productName: "",
    tagline: "",
    problem: "",
    milestones: {}
  })),
  videos: [],
  scores: {},
  streamUrl: "",
  pitchOrder: seedGroups.map(g => g.id),
  currentPitch: 0,
  pitched: []
};
function loadState(): HackathonState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const saved = JSON.parse(raw) as Partial<HackathonState>;
    const students = (saved.students ?? seedStudents).map(s => {
      const seed = seedStudents.find(x => x.id === s.id);
      return {
        joinedAt: null,
        ...s,
        // Stock placeholders saved earlier give way to real photos added to the seed list.
        photo: (s.photo && !s.photo.includes("/generated-images/") ? s.photo : seed?.photo || s.photo) || "",
        photoPosition: seed?.photoPosition ?? s.photoPosition,
        photoZoom: seed?.photoZoom ?? s.photoZoom,
        inviteToken: s.inviteToken || seed?.inviteToken || generateToken()
      };
    });
    // Seeded lineup is the source of truth; keep any photo/bio/group edits saved locally,
    // plus mentors added in the app. Old placeholder mentors are dropped.
    const savedMentors = (saved.mentors ?? []).filter(m => !retiredMentorIds.includes(m.id));
    const mentors = [...seedMentors.map(m => {
      const local = savedMentors.find(x => x.id === m.id);
      return local ? {
        ...m,
        photo: local.photo || m.photo,
        groupIds: local.groupIds?.length ? local.groupIds : m.groupIds,
        linkedin: local.linkedin || m.linkedin
      } : m;
    }), ...savedMentors.filter(m => !seedMentors.some(x => x.id === m.id))];
    return {
      ...initialState,
      ...saved,
      students,
      mentors
    };
  } catch {
    return initialState;
  }
}
const HackathonContext = createContext<HackathonContextValue | null>(null);
export function HackathonProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<HackathonState>(loadState);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage full — keep in memory */
    }
  }, [state]);
  const addStudent = useCallback((student: NewStudent) => {
    setState(s => ({
      ...s,
      students: [...s.students, {
        ...student,
        id: generateId("s"),
        inviteToken: generateToken(),
        joinedAt: null
      }]
    }));
  }, []);
  const updateStudent = useCallback((id: string, patch: Partial<Student>) => {
    setState(s => ({
      ...s,
      students: s.students.map(st => st.id === id ? {
        ...st,
        ...patch
      } : st)
    }));
  }, []);
  const removeStudent = useCallback((id: string) => {
    setState(s => ({
      ...s,
      students: s.students.filter(st => st.id !== id)
    }));
  }, []);
  const addMentor = useCallback((mentor: NewMentor) => {
    setState(s => ({
      ...s,
      mentors: [...s.mentors, {
        ...mentor,
        id: generateId("m"),
        inviteToken: generateToken(),
        joinedAt: null
      }]
    }));
  }, []);
  const updateMentor = useCallback((id: string, patch: Partial<Mentor>) => {
    setState(s => ({
      ...s,
      mentors: s.mentors.map(m => m.id === id ? {
        ...m,
        ...patch
      } : m)
    }));
  }, []);
  const removeMentor = useCallback((id: string) => {
    setState(s => ({
      ...s,
      mentors: s.mentors.filter(m => m.id !== id)
    }));
  }, []);
  const updateGroup = useCallback((id: number, patch: Partial<GroupProfile>) => {
    setState(s => ({
      ...s,
      groups: s.groups.map(g => g.id === id ? {
        ...g,
        ...patch
      } : g)
    }));
  }, []);
  const toggleMilestone = useCallback((groupId: number, milestoneId: string) => {
    setState(s => ({
      ...s,
      groups: s.groups.map(g => g.id === groupId ? {
        ...g,
        milestones: {
          ...g.milestones,
          [milestoneId]: !g.milestones[milestoneId]
        }
      } : g)
    }));
  }, []);
  const addVideo = useCallback(async (entry: VideoEntry, file: File) => {
    await saveVideo(entry.groupId, entry.day, file);
    setState(s => ({
      ...s,
      videos: [...s.videos.filter(v => !(v.groupId === entry.groupId && v.day === entry.day)), entry]
    }));
  }, []);
  const removeVideo = useCallback(async (groupId: number, day: number) => {
    await deleteVideo(groupId, day);
    setState(s => ({
      ...s,
      videos: s.videos.filter(v => !(v.groupId === groupId && v.day === day))
    }));
  }, []);
  const setScore = useCallback((groupId: number, score: GroupScore) => {
    setState(s => ({
      ...s,
      scores: {
        ...s.scores,
        [groupId]: score
      }
    }));
  }, []);
  const clearScores = useCallback(() => setState(s => ({
    ...s,
    scores: {},
    pitched: [],
    currentPitch: 0
  })), []);
  const setStreamUrl = useCallback((url: string) => setState(s => ({
    ...s,
    streamUrl: url
  })), []);
  const setPitchOrder = useCallback((order: number[]) => setState(s => ({
    ...s,
    pitchOrder: order,
    currentPitch: 0
  })), []);
  const setCurrentPitch = useCallback((index: number) => setState(s => ({
    ...s,
    currentPitch: index
  })), []);
  const markPitched = useCallback((groupId: number) => setState(s => ({
    ...s,
    pitched: s.pitched.includes(groupId) ? s.pitched : [...s.pitched, groupId]
  })), []);
  const value = useMemo(() => ({
    state,
    addStudent,
    updateStudent,
    removeStudent,
    addMentor,
    updateMentor,
    removeMentor,
    updateGroup,
    toggleMilestone,
    addVideo,
    removeVideo,
    setScore,
    clearScores,
    setStreamUrl,
    setPitchOrder,
    setCurrentPitch,
    markPitched
  }), [state, addStudent, updateStudent, removeStudent, addMentor, updateMentor, removeMentor, updateGroup, toggleMilestone, addVideo, removeVideo, setScore, clearScores, setStreamUrl, setPitchOrder, setCurrentPitch, markPitched]);
  return <HackathonContext.Provider value={value} data-id="element-766" data-mp="1zaej6kbkm3s1">{children}</HackathonContext.Provider>;
}
export function useHackathon(): HackathonContextValue {
  const ctx = useContext(HackathonContext);
  if (!ctx) throw new Error("useHackathon must be used within HackathonProvider");
  return ctx;
}